'use client'

import React, { useState } from 'react'
import styles from './styles.module.css'
import { processStrapiTextTags } from '@/components/StrapiContentRenderer'
import { convertRichTextToHTML } from '@/components/StrapiContentRenderer'

interface HorizontalItem {
  id?: number
  number?: string | number
  title?: string | any
  description?: string | any
}

interface HorizontalData {
  is_accordion?: boolean
  horisontal_item?: Array<HorizontalItem>
}

interface HorizontalProps {
  data: HorizontalData
}

const processText = (text: string | any): string => {
  if (!text) return ''
  
  if (typeof text === 'string') {
    return processStrapiTextTags(text)
  }
  
  if (Array.isArray(text)) {
    const html = convertRichTextToHTML(text)
    return processStrapiTextTags(html)
  }
  
  return processStrapiTextTags(String(text))
}

const Horizontal: React.FC<HorizontalProps> = ({ data }) => {
  const items = data.horisontal_item?.filter(
    item => item.number || item.title || item.description
  ) || []

  const isAccordion = data.is_accordion === true
  const [isOpen, setIsOpen] = useState(false)

  if (items.length === 0) {
    return null
  }

  const firstItem = items[0]
  const restItems = items.slice(1)

  const renderItem = (item: HorizontalItem, index: number) => {
    const number = item.number ? String(item.number) : ''
    const title = processText(item.title)
    const description = processText(item.description)

    if (!number && !title && !description) return null

    return (
      <div key={item.id || index} className={styles.achievement}>
        <div className={styles.achievementHeader}>
          {number && (
            <div className={styles.achievementNumber}>{number}</div>
          )}
          {title && (
            <h3 
              className={styles.achievementTitle}
              dangerouslySetInnerHTML={{ __html: title }}
            />
          )}
        </div>
        {description && (
          <p 
            className={styles.achievementDescription}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}
      </div>
    )
  }

  // Обычный режим - показываем все элементы
  if (!isAccordion) {
    return (
      <section className={styles.achievements}>
        {items.map((item, index) => renderItem(item, index))}
      </section>
    )
  }

  // Режим аккордеона
  const firstNumber = firstItem.number ? String(firstItem.number) : ''
  const firstTitle = processText(firstItem.title)
  const firstDescription = processText(firstItem.description)

  return (
    <section className={styles.achievements}>
      {/* Первый элемент всегда виден и кликабелен */}
      <div 
        className={`${styles.achievement} ${styles.achievementFirst} ${isOpen ? styles.achievementFirstOpen : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setIsOpen(!isOpen)
          }
        }}
      >
        <div className={styles.achievementHeader}>
          {firstNumber && (
            <div className={styles.achievementNumber}>{firstNumber}</div>
          )}
          {firstTitle && (
            <h3 
              className={styles.achievementTitle}
              dangerouslySetInnerHTML={{ __html: firstTitle }}
            />
          )}
          <button 
            className={styles.accordionButton}
            onClick={(e) => {
              e.stopPropagation()
              setIsOpen(!isOpen)
            }}
            aria-expanded={isOpen}
            aria-label={isOpen ? 'Скрыть' : 'Показать все'}
          >
          </button>
        </div>
        {firstDescription && (
          <p 
            className={styles.achievementDescription}
            dangerouslySetInnerHTML={{ __html: firstDescription }}
          />
        )}
      </div>

      {/* Остальные элементы - скрыты/показаны в зависимости от состояния */}
      {restItems.length > 0 && (
        <div className={`${styles.accordionContent} ${isOpen ? styles.accordionContentOpen : ''}`}>
          {restItems.map((item, index) => renderItem(item, index + 1))}
        </div>
      )}
    </section>
  )
}

Horizontal.displayName = 'Horizontal'

export default Horizontal

